/* DAC — <dac-symptom-tabs>
 *
 * Two modes, one component:
 *   data-mode="tabs"      desktop; one panel visible at a time, ARIA tablist
 *   data-mode="accordion" mobile drawer; tap a category to expand its cards
 *
 * Every tab is a real <a> pointing at its collection, so with JS off (or on a
 * modified click) the category still navigates. Plain left-clicks are
 * intercepted and swap the panel instead.
 *
 * Deliberately avoids <summary> and <button> for the toggles: Dawn's MenuDrawer
 * binds click handlers to every summary and button inside the header drawer and
 * would hijack them.
 */
(function () {
  if (customElements.get('dac-symptom-tabs')) return;

  class DacSymptomTabs extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.mode = this.dataset.mode === 'accordion' ? 'accordion' : 'tabs';
      this.tabs = Array.from(this.querySelectorAll('[data-symptom-tab]'));

      this.tabs.forEach((tab) => {
        tab.addEventListener('click', this.onTabClick.bind(this));
        tab.addEventListener('keydown', this.onKeyDown.bind(this));
      });
    }

    panelFor(tab) {
      const id = tab.getAttribute('data-symptom-tab');
      if (!id) return null;
      return this.querySelector(`[data-symptom-panel][id="${id}"]`);
    }

    onTabClick(event) {
      // Let ctrl/cmd/shift/middle clicks fall through to the collection page.
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;

      event.preventDefault();
      this.activate(event.currentTarget);
    }

    onKeyDown(event) {
      const key = event.key;

      // Space activates; Enter already fires a click on an anchor.
      if (key === ' ' || key === 'Spacebar') {
        event.preventDefault();
        this.activate(event.currentTarget);
        return;
      }

      if (this.mode !== 'tabs') return;

      const directions = { ArrowDown: 1, ArrowRight: 1, ArrowUp: -1, ArrowLeft: -1 };
      let index = this.tabs.indexOf(event.currentTarget);
      if (index === -1) return;

      if (directions[key]) {
        index = (index + directions[key] + this.tabs.length) % this.tabs.length;
      } else if (key === 'Home') {
        index = 0;
      } else if (key === 'End') {
        index = this.tabs.length - 1;
      } else {
        return;
      }

      event.preventDefault();
      this.activate(this.tabs[index]);
      this.tabs[index].focus();
    }

    activate(tab) {
      const panel = this.panelFor(tab);
      if (!panel) return;

      if (this.mode === 'accordion') {
        const wasOpen = tab.getAttribute('aria-expanded') === 'true';
        this.closeAll();
        if (!wasOpen) {
          tab.setAttribute('aria-expanded', 'true');
          panel.hidden = false;
          panel.classList.add('is-active');
        }
        return;
      }

      this.tabs.forEach((other) => {
        const otherPanel = this.panelFor(other);
        const isTarget = other === tab;

        other.classList.toggle('is-active', isTarget);
        other.setAttribute('aria-selected', isTarget ? 'true' : 'false');
        other.setAttribute('tabindex', isTarget ? '0' : '-1');

        if (otherPanel) {
          otherPanel.hidden = !isTarget;
          otherPanel.classList.toggle('is-active', isTarget);
        }
      });
    }

    closeAll() {
      this.tabs.forEach((tab) => {
        const panel = this.panelFor(tab);
        tab.setAttribute('aria-expanded', 'false');
        if (panel) {
          panel.hidden = true;
          panel.classList.remove('is-active');
        }
      });
    }
  }

  customElements.define('dac-symptom-tabs', DacSymptomTabs);
})();
